import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Video } from 'lucide-react';
import { DeleteEventButton } from './DeleteEventButton';

export const MemberChips = ({ members, selected, onToggle, testPrefix = 'invite' }) => (
  <div className="flex flex-wrap gap-1.5">
    {members.map((m) => {
      const on = selected.includes(m.id);
      return (
        <button key={m.id} type="button" onClick={() => onToggle(m.id)}
          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted border-border'}`}
          data-testid={`${testPrefix}-${m.id}`}>
          {m.name}
        </button>
      );
    })}
    {members.length === 0 && <span className="text-xs text-muted-foreground">No hay compañeros disponibles</span>}
  </div>
);

export const TeamMeetingDialog = ({ open, onOpenChange, form, setForm, members, onSave, saving, onDelete, deleting }) => {
  const toggle = (id) => setForm({ ...form, invitados: form.invitados.includes(id) ? form.invitados.filter((x) => x !== id) : [...form.invitados, id] });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="team-meeting-dialog">
        <DialogHeader><DialogTitle>{form.id ? 'Editar reunión' : 'Nueva reunión'}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Formulario de reunión</DialogDescription>
        <div className="py-2 space-y-3">
          <div>
            <Label>Título</Label>
            <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Revisión semanal" data-testid="meeting-title-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="meeting-date-input" />
            </div>
            <div>
              <Label>Inicio</Label>
              <Input type="time" value={form.hora_inicio} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} data-testid="meeting-start-input" />
            </div>
            <div>
              <Label>Fin</Label>
              <Input type="time" value={form.hora_fin} onChange={(e) => setForm({ ...form, hora_fin: e.target.value })} data-testid="meeting-end-input" />
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Enlace de Meet</Label>
            <Input value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} placeholder="https://meet.google.com/..." data-testid="meeting-meet-input" />
          </div>
          <div>
            <Label>Invitar compañeros</Label>
            <MemberChips members={members} selected={form.invitados} onToggle={toggle} />
          </div>
          <div>
            <Label>Nota (opcional)</Label>
            <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={2} placeholder="Orden del día, detalles..." data-testid="meeting-note-input" />
          </div>
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          {form.id && onDelete && (
            <DeleteEventButton title={form.titulo} deleting={deleting} onConfirm={() => onDelete(form)} testId="delete-meeting-btn"
              description={`La reunión desaparecerá de tu calendario${form.invitados?.length ? ' y del de las personas invitadas, que recibirán aviso de la cancelación' : ''}. Podrás deshacerlo durante unos minutos.`} />
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={onSave} disabled={saving || !form.titulo.trim() || !form.fecha} data-testid="save-meeting-btn">
              {saving ? 'Guardando...' : (form.id ? 'Guardar' : 'Crear reunión')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
